import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import Button from "src/components/buttons/Button";
import Dropdown from "src/components/dropdowns/Dropdown";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import ReactTable from "src/components/tables/react-table/ReactTable";
import { handleCsvExport } from "src/helpers/helpers";
import useDebounce from "src/hooks/useDebounce";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import DownwardIcon from "src/icons/DownwardIcon";
import { getActiveApp } from "src/redux/selectors/app";
import RecentActivity from "src/tabs/dashboard/recent-activity/RecentActivity";
import {
  ACTIVE_APP,
  PR_STATUS,
  PURCHASE_ORDER_STATUS,
} from "src/utils/constants";
import TotalAmountDetails from "../../billing/create/TotalAmountDetails";
import {
  useGetProcurementRequestSingle,
  useUpdatePurchaseOrderStatus,
} from "../api-purchase-orders";
import useCreatePurchaseOrderTableColumns from "./useCreatePurchaseOrderTableColumns";

const CreatePurchaseOrders = ({
  isInViewMode = false,
  label = "Procurement Request",
  activeTab,
}: {
  isInViewMode?: boolean;
  label?: string;
  activeTab?: string;
}) => {
  useSetActiveTab(activeTab ?? TAB_NAMES.PROCUREMENT_REQUEST);
  const params = useParams();
  const [length] = useState(10);
  const [page] = useState(1);
  const [isRejectModalOpen, setRejectModalOpen] = useState(false);
  const [isInCompleteModalOpen, setInCompleteModalOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText);
  const { successNotification, errorNotification } = useNotification();
  const activeApp = useSelector(getActiveApp);
  const [purchaseOrderStatus, setPurchaseOrderStatus] = useState(
    PR_STATUS.FULL_FILLED
  );

  const {
    isLoading,
    data,
    mutate: updatePurchaseOrderData,
  } = useGetProcurementRequestSingle({
    length,
    page,
    search: debouncedSearch,
    purchaseOrderId: params.purchaseOrderId,
  });

  const taxAndGST = useMemo(
    () => Number(data?.data?.tax ?? 0) + Number(data?.data?.gst ?? 0),
    [data?.data?.gst, data?.data?.tax]
  );

  const purchaseOrderData = useMemo(
    () => data?.data?.procurementRequestProducts ?? [],
    [data?.data]
  );

  const mappedPurchaseOrderData = useMemo(() => {
    return purchaseOrderData?.map((po: any) => ({
      totalPrice: po.quantity * po.offeredPrice,
      ...po,
    }));
  }, [purchaseOrderData]);

  const {
    isLoading: isUpdatePurchaseOrderStatusLoading,
    execute: updatePurchaseOrderStatus,
    isSuccess: isUpdatePurchaseOrderSuccess,
    error: isUpdatePurchaseOrderFailed,
  } = useUpdatePurchaseOrderStatus({
    purchaseOrderId: params.purchaseOrderId ?? "",
    vendorId: data?.data?.vendorId,
  });

  const columns = useCreatePurchaseOrderTableColumns();

  const subtotal = useMemo(
    () =>
      mappedPurchaseOrderData?.reduce(
        (acc: number, { totalPrice }: { totalPrice: number }) =>
          totalPrice + acc,
        0
      ),
    [mappedPurchaseOrderData]
  );

  const calculatedTax = useMemo(
    () => subtotal * (taxAndGST / 100),
    [subtotal, taxAndGST]
  );

  const handleStatusClick = (status: PR_STATUS) => {
    updatePurchaseOrderStatus({ status });
  };

  useEffect(() => {
    if (isUpdatePurchaseOrderFailed) {
      errorNotification("Something went wrong");
    }
    if (isUpdatePurchaseOrderSuccess) {
      successNotification("Updated successfully");
      updatePurchaseOrderData();
    }
  }, [
    errorNotification,
    isUpdatePurchaseOrderFailed,
    isUpdatePurchaseOrderSuccess,
    successNotification,
    updatePurchaseOrderData,
  ]);

  useEffect(() => {
    if (isUpdatePurchaseOrderSuccess) {
      setRejectModalOpen(false);
      setInCompleteModalOpen(false);
    }
  }, [isUpdatePurchaseOrderSuccess]);

  return (
    <>
      <ConfirmationModal
        isOpen={isRejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        isLoading={isUpdatePurchaseOrderStatusLoading}
        onSubmit={() => handleStatusClick(PR_STATUS.REJECTED)}
        title="Reject"
        heading="You Are about to reject request."
        subHeading="Do you really want to reject this request ? This process cannot be undone."
        buttonText="Reject"
      />
      <ConfirmationModal
        isOpen={isInCompleteModalOpen}
        onClose={() => setInCompleteModalOpen(false)}
        isLoading={isUpdatePurchaseOrderStatusLoading}
        onSubmit={() => handleStatusClick(PR_STATUS.IN_COMPLETED)}
        title="In Complete"
        heading="You Are about to incomplete request."
        subHeading="Do you really want to incomplete this request ? This process cannot be undone."
        buttonText="In Complete"
      />
      <div className="flex flex-col">
        <TabHeader
          label={label}
          onRefresh={() => updatePurchaseOrderData()}
          exportButton={{
            label: "Export",
            onClick: () => handleCsvExport(purchaseOrderData),
          }}
          printButton={{
            label: "Print",
            onClick: () => window.print(),
          }}
          // search={{
          //   onChange: (val) => setSearchText(val),
          //   value: searchText,
          // }}
        />
        <ReactTable
          className="!rounded-b-none mt-5"
          columns={columns}
          data={mappedPurchaseOrderData}
          isLoading={isLoading}
        />
        <div className="flex gap-6 bg-white p-4 rounded-b-[14px] border-t-[0.1px] border-solid border-lightGrey md:flex-row flex-col-reverse">
          <div className="w-full">
            <RecentActivity icon={<DownwardIcon />} />
          </div>

          <div className="flex flex-col gap-[14px] w-full pt-5">
            <div className="text-16px font-semibold py-2.5">
              Delivery Deadline: {Number(data?.data?.deadline)} Days
            </div>
            <TotalAmountDetails subtotal={subtotal} gst={calculatedTax} />
            {data?.data?.status === PR_STATUS.PROCESSING && !isInViewMode && (
              <div className="flex gap-[14px] justify-end">
                <Button
                  text="Reject"
                  danger
                  ghost
                  className="w-full"
                  onClick={() => setRejectModalOpen(true)}
                />
                <Button
                  text="Approve"
                  primary
                  className="w-full"
                  onClick={() => handleStatusClick(PR_STATUS.APPROVED)}
                />
              </div>
            )}
            {data?.data?.status === PR_STATUS.APPROVED &&
              activeApp === ACTIVE_APP.VENDOR && (
                <div className="flex flex-col gap-3">
                  <Dropdown
                    label="Status"
                    options={PURCHASE_ORDER_STATUS}
                    value={purchaseOrderStatus}
                    onChange={(value) =>
                      setPurchaseOrderStatus(value as PR_STATUS)
                    }
                    placeholder="Select Status"
                  />
                  <Button
                    text="Approve"
                    primary
                    className="w-full"
                    onClick={() => handleStatusClick(purchaseOrderStatus)}
                  />
                </div>
              )}
            {data?.data?.status === PR_STATUS.FULL_FILLED &&
              activeApp === ACTIVE_APP.STORE && (
                <div className="flex gap-[14px] justify-end">
                  <Button
                    text="In Completed"
                    danger
                    ghost
                    className="w-full"
                    onClick={() => setInCompleteModalOpen(true)}
                  />
                  <Button
                    text="Completed"
                    primary
                    className="w-full"
                    onClick={() => handleStatusClick(PR_STATUS.COMPLETED)}
                  />
                </div>
              )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CreatePurchaseOrders;
